import ProcedureStepTime from './ProcedureStepTime';
import React, { MouseEventHandler } from 'react';
import { Patient, PatientStatus } from '../../../../../types/Patient';
import Physician from '../../../../../types/Physician';
import { Procedure, ProcedureType } from '../../../../../types/Procedure';
import HelperAction from '../HelperAction';
import { Room } from '../../../../../types/Room';
import useTime from '../../../../../util/useTime';
import { format } from 'date-fns';
import ProcedureProgress from '../ProcedureProgress';
import { ButtonLabel, MainButton, TimeOutButton } from '../common';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { WithWorking } from '../OperationRoomTabletV1';
import { ProcedureStep } from '../../../../../types/ProcedureStep';
import {
  Box2,
  Box3,
  Box4,
  Content,
  Footer,
  Header,
  HeaderColumn,
  MainColumn,
  PersonName,
  PersonTitle,
  Root,
  SideColumn,
  Text1,
  Text2,
  Text3,
  Text5,
} from './styles';
import NextProcedures from './NextProcedures';
import { H3 } from '../../../../../se/components/typography';
import { Exit } from '../Modal';
import { ProcedureState } from '../util/procedureSteps';
import TimeOut2ActionButton from './TimeOut2ActionButton';
import NotifyButton, { ChatButton } from './NotifyButton';
import { Destination } from '../../../../../types/NotificationTemplate';
import { eventTime } from '../utils';
import { PROCEDURE_STATUSES } from '../../../../entities/patient/enums';
import AnesthesiologistOr from './AnethesiologistOr';
import { Box } from '@material-ui/core';

const ProcedureHandling = ({
  status,
  room,
  patient,
  physician,
  procedureType,
  editableAnesthesiologist,
  procedureState,
  nextProceduresInOperationRoom,
  notifyPhysician,
}: {
  status: string;
  room: Room & { status: string; patient: Patient & { duration?: string } };
  patient?: Patient & { duration?: string };
  physician?: Physician;
  procedureType?: ProcedureType;
  procedureState: ProcedureState | null;
  nextProceduresInOperationRoom: Procedure[];
  editableAnesthesiologist?: boolean;
  notifyPhysician: () => Promise<void>;
}) => {
  const currentTime = useTime(60 * 1000);

  const steps1 = (procedureState?.steps || []).slice(0, 4) || [];
  const steps2 = (procedureState?.steps || []).slice(4, 8) || [];

  const nextStepExists = !!procedureState?.next;

  return (
    <Root>
      <Header>
        <HeaderColumn first>
          <PersonTitle>Physician</PersonTitle>
          <PersonName>
            {physician?.name}
            {status === PatientStatus.InOr && (
              <NotifyButton
                trigger="PatientReady"
                destination={Destination.Physician}
                notify={notifyPhysician}
                profileMessagingEnabled={!!physician?.shouldSendNotification}
                hasPhoneNumber={!!physician?.notificationNumbers}
                notified={!!eventTime(PROCEDURE_STATUSES.READY, patient)}
              />
            )}
          </PersonName>
          <Text1>{procedureType?.name}</Text1>
        </HeaderColumn>

        <HeaderColumn>
          <PersonTitle>Anesthesiologist</PersonTitle>
          <PersonName>
            {patient?.procedure?.anesthesiologist?.name}
            {!!patient?.procedure?.id && (
              <AnesthesiologistOr
                procedureId={patient?.procedure?.id}
                editableAnesthesiologist={editableAnesthesiologist}
              />
            )}
          </PersonName>
          <Text1>{`Anesthesia type ${patient?.procedure?.anesthesiaType}`}</Text1>
        </HeaderColumn>

        <HeaderColumn>
          <PersonTitle>Patient</PersonTitle>
          <PersonName>
            {patient?.name}
            <ChatButton patient={patient} />
          </PersonName>
          <Text1>Duration {patient?.duration}</Text1>
        </HeaderColumn>
      </Header>
      <Content>
        <SideColumn gridRowNumber={1}>
          {steps1.map((procedureStep: ProcedureStep & { highlight: boolean; show: boolean }, index: number) => (
            <ProcedureStepTime key={procedureStep?.id} index={index} procedureStep={procedureStep} patient={patient} />
          ))}
        </SideColumn>
        <MainColumn gridRowNumber={3}>
          <HelperAction WrapperComponent={Box2} />
          <ProcedureProgress status={room?.status} />
          {nextStepExists ? (
            <Box display="flex" justifyContent="center">
              <TimeOutButton
                style={{ maxWidth: '11rem' }}
                onClick={procedureState?.prev as MouseEventHandler<HTMLButtonElement> | undefined}
                disabled={!procedureState?.prev}
              >
                <WithWorking working={false} size={'4rem'}>
                  <Box fontSize={'6.5em'}>
                    <ChevronLeftIcon fontSize={'inherit'} />
                  </Box>
                </WithWorking>
                <ButtonLabel>{'Undo'}</ButtonLabel>
              </TimeOutButton>

              <MainButton
                style={{ maxWidth: '11rem' }}
                onClick={procedureState?.next as MouseEventHandler<HTMLButtonElement> | undefined}
                disabled={!procedureState?.next}
              >
                <WithWorking working={false} size={'4rem'}>
                  <Box fontSize={'6.5em'}>
                    <ChevronRightIcon fontSize={'inherit'} />
                  </Box>
                </WithWorking>
                <ButtonLabel>{procedureState?.nextLabel}</ButtonLabel>
              </MainButton>
            </Box>
          ) : (
            <Box4>
              <H3 style={{ opacity: '.5' }}>No further action needed</H3>
              <Text5>
                This procedure will be automatically marked as complete <br /> as soon as the patient leaves the room.
              </Text5>
              <Exit
                style={{ minHeight: '1em' }}
                onClick={procedureState?.prev as MouseEventHandler<HTMLButtonElement> | undefined}
                disabled={false}
              >
                <WithWorking working={false}>Undo</WithWorking>
              </Exit>
            </Box4>
          )}
        </MainColumn>
        <SideColumn gridRowNumber={2}>
          {steps2.map((procedureStep: ProcedureStep & { highlight: boolean; show: boolean }, index: number) => (
            <ProcedureStepTime key={procedureStep?.id} index={index} procedureStep={procedureStep} patient={patient} />
          ))}
        </SideColumn>
      </Content>
      <Footer>
        <Box3>
          <NextProcedures nextProceduresInOperationRoom={nextProceduresInOperationRoom} />
          <Text2>{room?.name}</Text2>
          <TimeOut2ActionButton patient={patient} status={status} />
        </Box3>
        <Text3>{format(currentTime, 'HH:mm')}</Text3>
      </Footer>
    </Root>
  );
};

export default ProcedureHandling;
